/**
 * Panel Quote import list helpers.
 *
 * Pure functions only — no Appwrite, no React. Searching and column sorting for
 * the "Import from Panel Quote" dialog live here so the comparators stay in one
 * place as columns are added, and so the behaviour can be checked without
 * mounting a dialog.
 */

// The columns the import table shows, in order. `sortKey` is the field a header
// sorts on, `sortType` picks the comparator, and `defaultDirection` is what the
// first click applies — descending for photos, dates and amounts, because
// most/newest/biggest is what people reach for on those.
export const PANEL_QUOTE_COLUMNS = [
  { key: 'select', label: 'Job', sortKey: 'jobNumber', sortType: 'text', defaultDirection: 'asc' },
  { key: 'customer', label: 'Customer', sortKey: 'customerName', sortType: 'text', defaultDirection: 'asc' },
  { key: 'vehicle', label: 'Vehicle', sortKey: 'vehicle', sortType: 'text', defaultDirection: 'asc' },
  { key: 'status', label: 'Status', sortKey: 'jobStatus', sortType: 'text', defaultDirection: 'asc' },
  { key: 'photos', label: 'Photos', sortKey: 'numPhotos', sortType: 'number', defaultDirection: 'desc' },
  { key: 'date', label: 'Date', sortKey: 'date', sortType: 'date', defaultDirection: 'desc' },
  { key: 'amount', label: 'Amount', sortKey: 'amountPrimary', sortType: 'number', defaultDirection: 'desc', align: 'right' }
];

// No column sorted: the rows stay in the order Panel Quote listed them, which
// is what `sortOrder` records.
export const NO_SORT = { key: null, direction: null };

const columnForSortKey = (sortKey) =>
  PANEL_QUOTE_COLUMNS.find((column) => column.sortKey === sortKey) || null;

/**
 * The next sort state for a header click: the column's default direction, then
 * the opposite, then back to Panel Quote's own order.
 */
export const nextSort = (current, column) => {
  if (!column?.sortKey) return NO_SORT;
  if (current?.key !== column.sortKey) {
    return { key: column.sortKey, direction: column.defaultDirection };
  }
  if (current.direction === column.defaultDirection) {
    return {
      key: column.sortKey,
      direction: column.defaultDirection === 'asc' ? 'desc' : 'asc'
    };
  }
  return NO_SORT;
};

// The fields a search looks at. The insurer and the customer are separate
// fields on a Panel Quote job, as are the job and claim numbers, so all of them
// are worth searching — someone who knows the insurer and someone who only has
// a claim number both find their row.
const SEARCH_FIELDS = [
  'jobNumber', 'claimNumber', 'customerName', 'customerSecondary',
  'vehicle', 'jobStatus', 'claimStatus', 'date'
];

export const searchTerms = (search) =>
  String(search || '').trim().toLowerCase().split(/\s+/).filter(Boolean);

/**
 * Every term has to land somewhere on the row, so "IAG hilux" narrows instead
 * of widening — the insurer and the vehicle sit in different columns.
 */
export const matchesSearch = (quote, terms) => {
  if (!terms || terms.length === 0) return true;
  const haystack = SEARCH_FIELDS
    .map((field) => quote[field])
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return terms.every((term) => haystack.includes(term));
};

/**
 * Panel Quote lists the date as display text rather than ISO, so read a
 * day-first date out of it before falling back to whatever Date can parse.
 * Returns null for anything unreadable.
 */
export const parseListDate = (value) => {
  if (!value) return null;
  const text = String(value).trim();

  // A year-first date is unambiguous, and reading it here keeps the day-first
  // pass below from taking "2026-08-05" apart from the wrong end.
  if (/^\d{4}-\d{1,2}-\d{1,2}/.test(text)) {
    const isoParsed = Date.parse(text);
    if (!Number.isNaN(isoParsed)) return isoParsed;
  }

  const dayFirst = text.match(/\b(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})\b/);
  if (dayFirst) {
    const [, day, month, year] = dayFirst;
    // A month past 12 means the date wasn't day-first after all; leave those
    // to Date, which reads them month-first.
    if (Number(month) >= 1 && Number(month) <= 12 && Number(day) >= 1 && Number(day) <= 31) {
      const fullYear = year.length === 2 ? 2000 + Number(year) : Number(year);
      return new Date(fullYear, Number(month) - 1, Number(day)).getTime();
    }
  }

  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * The value a column sorts on. null means the row has nothing in that column;
 * those sink to the bottom in both directions rather than crowding whichever
 * end counts as smallest.
 */
export const sortValue = (quote, column) => {
  const raw = quote[column.sortKey];
  if (column.sortType === 'number') {
    const number = typeof raw === 'number'
      ? raw
      : Number.parseFloat(String(raw ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(number) ? number : null;
  }
  if (column.sortType === 'date') return parseListDate(raw);
  const text = String(raw ?? '').trim();
  return text || null;
};

// numeric: true so job numbers, which arrive as digit strings, don't sort "10"
// ahead of "9".
const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

/**
 * The rows to show: those matching every search term, in the sorted order.
 * Ties fall back to `sortOrder`, which keeps the sort stable and leaves equal
 * rows in the order Panel Quote listed them.
 */
export const filterAndSortQuotes = (quotes, { terms = [], sort = NO_SORT } = {}) => {
  const filtered = terms.length === 0
    ? quotes
    : quotes.filter((quote) => matchesSearch(quote, terms));

  const column = columnForSortKey(sort?.key);
  if (!column) return filtered;

  return [...filtered].sort((a, b) => {
    const aValue = sortValue(a, column);
    const bValue = sortValue(b, column);
    if (aValue === null || bValue === null) {
      if (aValue !== bValue) return aValue === null ? 1 : -1;
    } else {
      const comparison = typeof aValue === 'string'
        ? collator.compare(aValue, bValue)
        : aValue - bValue;
      if (comparison !== 0) return sort.direction === 'asc' ? comparison : -comparison;
    }
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  });
};
